import axios, { type AxiosError } from "axios";

const DEFAULT_IGNORED_ROOT_KEYS = new Set([
  "code",
  "message",
  "status",
  "statusCode",
]);

type ExtractOptions = {
  ignoredRootKeys?: ReadonlyArray<string> | Set<string>;
};

type HandleApiErrorOptions = {
  defaultMessage: string;
  networkMessage?: string;
  label?: string;
  logResponseDetails?: boolean;
};

type ApiErrorPayload = {
  code?: unknown;
  error_code?: unknown;
  error?: {
    code?: unknown;
    error_code?: unknown;
  };
  errors?:
    | {
        code?: unknown;
        error_code?: unknown;
      }
    | Array<{
        code?: unknown;
        error_code?: unknown;
      }>;
};

type UploadQuotaExceededErrorPayload = {
  message?: unknown;
  limit_bytes?: unknown;
  used_bytes?: unknown;
  requested_bytes?: unknown;
};

export const extractValidationErrors = (
  data: unknown,
  options: ExtractOptions = {},
): Record<string, string[]> => {
  const ignoredKeys = options.ignoredRootKeys
    ? new Set(options.ignoredRootKeys)
    : DEFAULT_IGNORED_ROOT_KEYS;

  const result: Record<string, string[]> = {};

  const visit = (node: unknown, path: string[] = []) => {
    if (node == null) return;

    if (typeof node === "string") {
      if (path.length === 0) return;
      if (path.length === 1 && ignoredKeys.has(path[0])) return;
      const key = path[path.length - 1];
      if (key) {
        result[key] = [...(result[key] ?? []), node];
      }
      return;
    }

    if (Array.isArray(node)) {
      const stringItems = node.filter(
        (item): item is string => typeof item === "string",
      );
      if (stringItems.length > 0) {
        const key = path[path.length - 1];
        if (key) {
          result[key] = [...(result[key] ?? []), ...stringItems];
          return;
        }
      }

      node.forEach((item) => {
        visit(item, path);
      });
      return;
    }

    if (typeof node === "object") {
      Object.entries(node).forEach(([childKey, value]) => {
        if (path.length === 0 && ignoredKeys.has(childKey)) {
          return;
        }
        visit(value, [...path, childKey]);
      });
    }
  };

  visit(data);
  return result;
};

export class ApiValidationError extends Error {
  fieldErrors: Record<string, string[]>;
  status: number;

  constructor(
    message: string,
    fieldErrors: Record<string, string[]>,
    status = 422,
  ) {
    super(message);
    this.name = "ApiValidationError";
    this.fieldErrors = fieldErrors;
    this.status = status;
  }
}

export class ApiHttpError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiHttpError";
    this.status = status;
  }
}

const toStringOrNull = (value: unknown): string | null => {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
};

export const extractApiErrorCode = (data: unknown): string | null => {
  if (!data || typeof data !== "object") {
    return null;
  }

  const payload = data as ApiErrorPayload;

  const direct = toStringOrNull(payload.code);
  if (direct) {
    return direct;
  }

  const directSnakeCase = toStringOrNull(payload.error_code);
  if (directSnakeCase) {
    return directSnakeCase;
  }

  const nestedError = toStringOrNull(payload.error?.code);
  if (nestedError) {
    return nestedError;
  }

  const nestedSnakeCaseError = toStringOrNull(payload.error?.error_code);
  if (nestedSnakeCaseError) {
    return nestedSnakeCaseError;
  }

  if (Array.isArray(payload.errors)) {
    const first = payload.errors.find(
      (item) => toStringOrNull(item?.code) || toStringOrNull(item?.error_code),
    );
    return toStringOrNull(first?.code) ?? toStringOrNull(first?.error_code);
  }

  return (
    toStringOrNull(payload.errors?.code) ??
    toStringOrNull(payload.errors?.error_code)
  );
};

export const MONTHLY_UPLOAD_LIMIT_EXCEEDED_CODE =
  "MONTHLY_UPLOAD_LIMIT_EXCEEDED";

export type UploadQuotaExceededDetail = {
  code: typeof MONTHLY_UPLOAD_LIMIT_EXCEEDED_CODE;
  limitBytes: number | null;
  usedBytes: number | null;
  requestedBytes: number | null;
  message?: string;
};

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const extractUploadQuotaExceededDetail = (
  data: unknown,
): UploadQuotaExceededDetail | null => {
  if (!data || typeof data !== "object") {
    return null;
  }

  const payload = data as UploadQuotaExceededErrorPayload;

  if (extractApiErrorCode(data) !== MONTHLY_UPLOAD_LIMIT_EXCEEDED_CODE) {
    return null;
  }

  return {
    code: MONTHLY_UPLOAD_LIMIT_EXCEEDED_CODE,
    limitBytes: toNullableNumber(payload.limit_bytes),
    usedBytes: toNullableNumber(payload.used_bytes),
    requestedBytes: toNullableNumber(payload.requested_bytes),
    message: typeof payload.message === "string" ? payload.message : undefined,
  };
};

export const getUploadQuotaExceededDetailFromError = (
  error: unknown,
): UploadQuotaExceededDetail | null => {
  if (!isAxiosError(error)) {
    return null;
  }
  return extractUploadQuotaExceededDetail(error.response?.data);
};

export const isUploadQuotaExceededError = (error: unknown): boolean =>
  getUploadQuotaExceededDetailFromError(error) !== null;

export const getApiErrorStatus = (error: unknown): number | null => {
  if (error instanceof ApiHttpError || error instanceof ApiValidationError) {
    return error.status;
  }

  if (isAxiosError(error)) {
    const responseCode = (
      error.response?.data as { code?: unknown } | undefined
    )?.code;
    if (typeof responseCode === "number") {
      return responseCode;
    }
    if (typeof responseCode === "string" && /^\d+$/.test(responseCode)) {
      return Number(responseCode);
    }
    return error.response?.status ?? null;
  }

  if (error && typeof error === "object") {
    const status = (error as { status?: unknown }).status;
    const code = (error as { code?: unknown }).code;
    const candidate = status ?? code;
    if (typeof candidate === "number") {
      return candidate;
    }
    if (typeof candidate === "string" && /^\d+$/.test(candidate)) {
      return Number(candidate);
    }
  }

  return null;
};

export const isForbiddenError = (error: unknown): boolean => {
  return getApiErrorStatus(error) === 403;
};

export const handleAxiosError = (
  error: AxiosError,
  defaultMessage: string,
  options: ExtractOptions = {},
): never => {
  if (!error.response) {
    throw new Error("Please check your connection");
  }

  if (error.response.status === 422 && error.response.data) {
    const quotaDetail = extractUploadQuotaExceededDetail(error.response.data);
    if (quotaDetail !== null) {
      throw new ApiValidationError(
        quotaDetail.message ?? "Monthly upload limit exceeded.",
        {
          code: [MONTHLY_UPLOAD_LIMIT_EXCEEDED_CODE],
        },
        error.response.status,
      );
    }

    const fieldErrors = extractValidationErrors(error.response.data, options);
    const flattenedMessages = Object.values(fieldErrors).flat();
    const validationMessage =
      flattenedMessages[0] ||
      (typeof (error.response.data as { message?: unknown }).message ===
      "string"
        ? ((error.response.data as { message?: string }).message ??
          "Validation failed")
        : "Validation failed");

    throw new ApiValidationError(
      validationMessage,
      fieldErrors,
      error.response.status,
    );
  }

  const errorData = error.response.data as { message?: string } | undefined;
  throw new ApiHttpError(
    errorData?.message || defaultMessage,
    error.response.status,
  );
};

export const isAxiosError = (error: unknown): error is AxiosError =>
  axios.isAxiosError(error);

export const logApiError = (
  error: unknown,
  {
    label = "API Error",
    logResponseDetails = false,
  }: Pick<HandleApiErrorOptions, "label" | "logResponseDetails"> = {},
) => {
  if (isAxiosError(error)) {
    console.error(`${label}:`, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      ...(logResponseDetails
        ? {
            headers: error.response?.headers,
            requestUrl: error.config?.url,
            requestMethod: error.config?.method,
          }
        : {}),
    });
    return;
  }

  console.error(`${label}:`, error);
};

export const handleApiError = (
  error: unknown,
  {
    defaultMessage,
    networkMessage = "Please check your connection",
    label = "API Error",
    logResponseDetails = false,
  }: HandleApiErrorOptions,
): never => {
  if (isAxiosError(error) && !error.response) {
    throw new Error(networkMessage);
  }

  logApiError(error, { label, logResponseDetails });
  return handleUnknownError(error, defaultMessage);
};

export const handleUnknownError = (
  error: unknown,
  defaultMessage: string,
): never => {
  if (isAxiosError(error)) {
    return handleAxiosError(error, defaultMessage);
  }

  throw error instanceof Error ? error : new Error(defaultMessage);
};
